import { useState, KeyboardEvent } from 'react';
import { X } from 'lucide-react';
import { cn } from '@ai-tutor/ui';

interface TagInputProps {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  maxTags?: number;
  disabled?: boolean;
  className?: string;
}

export function TagInput({
  value,
  onChange,
  placeholder = 'Add a tag',
  maxTags = 20,
  disabled = false,
  className,
}: TagInputProps) {
  const [draft, setDraft] = useState('');

  const commitDraft = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    if (value.includes(trimmed)) {
      setDraft('');
      return;
    }
    if (value.length >= maxTags) return;
    onChange([...value, trimmed]);
    setDraft('');
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commitDraft();
    } else if (e.key === 'Backspace' && draft === '' && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };

  const removeTag = (tag: string) => {
    onChange(value.filter(t => t !== tag));
  };

  const hasTags = value.length > 0;

  return (
    <div className={className}>
      <div
        className={cn(
          // `bg-card`, not the `bg-background` that globals.css nominates for
          // inputs.
          //
          // Both call sites are inside a `Modal`, whose own surface is
          // `bg-card`. The token contract says an input should read as
          // recessed against the page — but against a CARD it reads as a
          // darker patch, and a single recessed well next to flush siblings
          // reads as a bug rather than as depth. That is how it was reported.
          //
          // This is only coherent because the siblings agree: FolderFormModal's
          // Name was already `bg-card`, and EditLessonDetailsModal's Title and
          // Overview were moved onto it in the same change. Add a third call
          // site on a non-card surface and this needs to become a prop rather
          // than a constant.
          // Below `sm` the text field is the box's full 44px height, so a tap
          // anywhere on the row lands in it; the box keeps 2px of padding.
          'flex min-h-[3rem] flex-wrap items-center gap-1.5 rounded-md border border-input bg-card p-2 transition-colors focus-within:border-brand-400 dark:focus-within:border-primary max-sm:py-0.5',
          disabled && 'cursor-not-allowed opacity-50'
        )}
      >
        {value.map(tag => (
          <span
            key={tag}
            className={cn(
              'group/tag inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium',
              'bg-muted text-foreground ring-1 ring-border'
            )}
          >
            {tag}
            {!disabled && (
              <button
                type='button'
                onClick={() => removeTag(tag)}
                className={cn(
                  // A 16px glyph in the chip, tapped as 44 on a phone.
                  'tap-target rounded-full p-0.5 text-muted-foreground transition-colors',
                  'hover:bg-accent hover:text-foreground'
                )}
                aria-label={`Remove ${tag}`}
              >
                <X className='h-3 w-3' />
              </button>
            )}
          </span>
        ))}
        <input
          type='text'
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={commitDraft}
          disabled={disabled || value.length >= maxTags}
          placeholder={hasTags ? 'Add another tag…' : placeholder}
          className={cn(
            // 16px type on a phone: iOS Safari zooms the page into any
            // field set smaller.
            'min-w-[8rem] flex-1 bg-transparent px-1 py-0.5 text-base max-sm:min-h-11 sm:text-sm',
            'text-foreground placeholder:text-muted-foreground/60',
            'border-none',
            // `!important` to beat the base-layer focus-ring rule in
            // globals.css that targets every input:focus-visible.
            '!outline-none focus:!outline-none focus:!ring-0 focus:!ring-offset-0',
            'focus-visible:!outline-none focus-visible:!ring-0 focus-visible:!ring-offset-0'
          )}
        />
      </div>
      <p className='mt-1.5 text-xs text-muted-foreground'>
        Press{' '}
        <kbd className='rounded-sm bg-muted px-1 py-0.5 text-xs font-medium text-foreground'>
          Enter
        </kbd>{' '}
        or{' '}
        <kbd className='rounded-sm bg-muted px-1 py-0.5 text-xs font-medium text-foreground'>
          ,
        </kbd>{' '}
        to add
      </p>
    </div>
  );
}
